import { useEffect, useState } from 'react';
import FixedImageGrid from './FixedImageGrid';
import { isAdminMode } from '../utils/auth';
import { Box, Fade, Typography } from '@mui/material';

interface AboutContent {
  title: string;
  schoolName: string;
  description: string;
}

const AboutUs = () => {
  const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

  const [editMode, setEditMode] = useState<boolean>(false);
  const [content, setContent] = useState<AboutContent | null>(null);
  const [editableContent, setEditableContent] = useState<AboutContent | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/about`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setContent(data);
        setEditableContent(data);
      } catch (error) {
        console.error('Fetch error:', error);
        setContent({
          title: "Our School",
          schoolName: "EverGreen School",
          description: "Default description"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (field: keyof AboutContent, value: string) => {
    if (!editableContent) return;

    setEditableContent({
      ...editableContent,
      [field]: value
    });
  };

  const handleSave = async () => {
    if (!editableContent) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/api/about`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editableContent)
      });
      
      if (!response.ok) throw new Error('Failed to update content');
      
      setContent(editableContent);
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update content:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditableContent(content);
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!content || !editableContent) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <p className="text-red-500">Error loading content.</p>
      </div>
    );
  }

  return (
    <div className="  w-full">
      <div className="md:w-[80%] w-[90%] mx-auto">
        {/* Edit Button */}
        {isAdminMode() && (
          <button
            onClick={() => setEditMode(true)}
            className="absolute top-4 right-4 z-10 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium 
                     hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Edit
          </button>
        )}

        <div className=" space-y-6">
          {/* Title */}
          <Fade in timeout={800}>
            <Box className="text-center mb-12">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
                >
                  About Us
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: 'Black',
                  fontSize: '2rem',
                  fontWeight: 600,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Ever Green Senior Secondary School
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
Ever Green Senior Secondary School was established on 13th July 1996 by Late Sh. L. D. Pathak in Haldwani city of Uttarakhand in India. Ever Green is a premier English Medium educational institution affiliated to C.B.S.E., New Delhi which is owned by a registered organisation "EDUCATIONAL WELFARE SOCIETY", Tularampur. It is the Best CBSE School in Lalkuan Haldwani, located at Sri Ganga Vihar Colony, Beriparao, Vill- Tularampur, P.O.- Motahaldu, Teh- Lalkuan, Nainital, Uttarakhand on Bareilly Nainital Highway (NH- 109)              </Typography>
            </Box>
          </Fade>

          {/* Image Grid */}
          <div className="transform transition-all duration-500 ease-out delay-300">
            <div className="rounded-xl overflow-hidden shadow-sm">
              <FixedImageGrid />
            </div>
          </div>

          {/* More Button */}
          <div className="flex justify-end transform transition-all duration-500 ease-out delay-400">
            <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium 
                             hover:bg-blue-700 transform hover:scale-105 hover:-translate-y-1 
                             transition-all duration-200 shadow-md hover:shadow-lg
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              More
            </button>
          </div>

          {/* Action Buttons */}
          {editMode && (
            <div className="flex justify-center gap-4 pt-4 border-t border-gray-100 
                          animate-fade-in transform transition-all duration-300">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium 
                         hover:bg-green-700 transform hover:scale-105 transition-all duration-200 
                         focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                         flex items-center gap-2"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Save
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-6 py-3 border-2 border-red-500 text-red-500 rounded-lg font-medium 
                         hover:bg-red-500 hover:text-white transform hover:scale-105 transition-all duration-200 
                         focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <style >{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;